import { computed } from 'vue'
import { useNuxtApp, useRuntimeConfig } from '#app'
import type { BrandConfig, BrandFeatures, BrandId } from '~/server/config/siteConfig'
import { brands, getBrandIdForBaseUrl } from '~/server/config/siteConfig'

/**
 * Single source of truth for brand identity in components.
 *
 * Prefer this over reading `$siteConfig.brand` and string-comparing.
 *
 *   const { is, features, config } = useBrand()
 *   if (is('tripening')) { ... }
 *   if (features.value.blog) { ... }
 *
 * `useState('siteConfig')` is populated by plugins/siteConfig.ts. We fall
 * back to runtimeConfig.public.siteName so this composable also works in
 * contexts where the plugin hasn't run yet (e.g. inside other plugins).
 */
export function useBrand() {
    const nuxt = useNuxtApp()
    const runtimeConfig = useRuntimeConfig()

    const config = computed<BrandConfig>(() => {
        const fromState = (nuxt.$siteConfig as { value?: BrandConfig } | undefined)?.value
        if (fromState && fromState.id) return fromState
        const id = getBrandIdForBaseUrl(runtimeConfig.public?.siteName as string | undefined)
        return brands[id]
    })

    const id = computed<BrandId>(() => config.value.id)
    const features = computed<BrandFeatures>(() => config.value.features)
    const theme = computed(() => config.value.theme)

    function is(...candidates: BrandId[]): boolean {
        return candidates.includes(id.value)
    }

    function feature<K extends keyof BrandFeatures>(name: K): BrandFeatures[K] {
        return features.value[name]
    }

    return { config, id, features, theme, is, feature }
}
